import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { useManagementGetCouriers } from '@apps-common/apis/management/courier/get-couriers'
import { useManagementCompleteOrder } from '@apps-common/apis/management/order/complete-order'
import { QUERY_GET_ORDER_DETAILS, QUERY_SEARCH_ORDERS, useGetOrderDetails } from '@apps-common/apis/portal/orders'
import { QUERY_SEARCH_ORDERS_COUNT } from '@apps-common/apis/portal/orders/search-orders-count'
import { QUERY_SEARCH_ORDERS_INFINITE } from '@apps-common/apis/portal/orders/search-orders-infinite'
import { CardContainer } from '@apps-common/components/common'
import { ButtonRoundedSm } from '@apps-common/components/elements/button'
import { InputSM, SelectInputSM } from '@apps-common/components/elements/form'
import { VertSpacerLg, VertSpacerMd } from '@apps-common/components/layout'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { OrderStatus } from '@apps-common/config/orders'
import { queryClient } from '@apps-common/lib'
import { IOrderCompleteModel } from '@apps-common/types/management/order'

interface IProps {
  loading: boolean
  updating: boolean
  orderId: string
  isUpdatingCallback: (isUpdating: boolean) => void
}

export const ManagementOrderDetailsComplete: React.FC<IProps> = ({ loading, updating, orderId, isUpdatingCallback }) => {
  const {
    data: order,
    isLoading: orderIsLoading,
    isError: orderIsError
  } = useGetOrderDetails({ config: { enabled: orderId !== undefined }, id: orderId })
  const { data: couriers, isLoading: couriersIsLoading, isError: couriersIsError } = useManagementGetCouriers({ config: {}, name: '' })
  const { mutate: completeOrder, isPending: completeOrderIsPending } = useManagementCompleteOrder(true, () => {
    queryClient.refetchQueries({ queryKey: QUERY_GET_ORDER_DETAILS(orderId) })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_COUNT() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS() })
    queryClient.refetchQueries({ queryKey: QUERY_SEARCH_ORDERS_INFINITE() })
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors }
  } = useForm<IOrderCompleteModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const onSubmit = (values: IOrderCompleteModel) => {
    completeOrder({ model: { ...values, orderId } })
  }

  useEffect(() => {
    isUpdatingCallback(completeOrderIsPending || isSubmitting)
  }, [completeOrderIsPending, isSubmitting])

  const canComplete = useMemo(() => {
    const requiredUnits =
      order?.orderLine?.reduce((acc, curr) => acc + (curr?.productActionRequired ? curr?.productQuantity || 0 : 0), 0) || 0

    return requiredUnits === 0 || requiredUnits <= (order?.units?.length || 0)
  }, [order])

  const isLoading = loading || orderIsLoading || orderIsError || couriersIsLoading || couriersIsError

  return (
    <AnimatePresence>
      {order?.order?.status === OrderStatus.pending && canComplete && (
        <motion.div key='management-order-details-complete' {...motionFadeIn(0.25)}>
          <CardContainer bgClasses='bg-surface pb-2'>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <div className='grid sm:grid-cols-2 gap-[20px]'>
                <SelectInputSM
                  key='courierId'
                  type='text'
                  placeholder='Select courier'
                  label='Courier'
                  id='courierId'
                  showLabel
                  loading={isLoading}
                  disabled={updating || order?.order?.pickUp || !canComplete}
                  registration={register(`courierId`, {
                    validate: (value: string) => {
                      if (!order?.order?.pickUp && value?.length === 0) {
                        return 'Required'
                      }

                      return true
                    }
                  })}
                  options={[
                    { label: 'Select Courier', value: '' },
                    ...(couriers?.map((c) => ({ label: c.courierName, value: c.courierId })) || [])
                  ]}
                  errors={errors?.courierId as any}
                />
                <InputSM
                  type='text'
                  placeholder='Tracking number'
                  label='Tracking number'
                  id='trackingNumber'
                  showLabel
                  loading={isLoading}
                  disabled={updating || order?.order?.pickUp || !canComplete}
                  registration={register('trackingNumber', {
                    validate: (value: string) => {
                      if (!order?.order?.pickUp && value?.length === 0) {
                        return 'Required'
                      }

                      return true
                    }
                  })}
                  errors={errors.trackingNumber as any}
                />
              </div>
              <div className='flex items-center justify-end'>
                <ButtonRoundedSm
                  type='submit'
                  loading={isLoading || updating || !canComplete}
                  className='action-success grow-0 mt-2'
                  text='Complete'
                />
              </div>
            </form>
            <VertSpacerMd />
          </CardContainer>
          <VertSpacerLg />
        </motion.div>
      )}
    </AnimatePresence>
  )
}
